/**
 * Die Figurensymbole — **beschlossen am 2026-10-05 vom User** (Symbole statt Kürzel).
 *
 * **Warum Symbole.** Ein Kürzel ist eine Abkürzung in *einer* Sprache: „Lä" und „St" muss man
 * lernen, und in einer englischen Oberfläche hießen sie anders. Ein Symbol ist sprachunabhängig
 * (`CLAUDE.md` Regel 6) und schneller zu erfassen — auf dem Brett stehen bis zu 54 Figuren, und
 * die Klasse muss im Vorbeisehen erkennbar sein.
 *
 * **Jedes Symbol zeigt, was die Figur *tut*, nicht wie sie heißt:**
 *
 * | Klasse | Form | warum |
 * |---|---|---|
 * | Fähnrich | Fahne am Stab | er trägt das Ziel — er punktet im Zentrum (§19) |
 * | Wächter | Schild | er steht und schlägt auf Distanz 2 (§15) |
 * | Läufer | Doppelwinkel | zwei Pfeilspitzen: unbegrenzt gerade (§16) |
 * | Springer | Bogen über einem Punkt | er überspringt, was dazwischen steht (§17) |
 * | Stoßer | Wand mit Pfeil | er schiebt, statt zu schlagen (§18) |
 *
 * **Gezeichnet in einem Quadrat von −10 bis 10** und über `r` skaliert. Damit ist jedes Symbol
 * bei jeder Feldgröße gleich proportioniert, und die Formen bleiben innerhalb des Kreises, auf
 * dem sie liegen — eine Form, die über den Rand ragt, sieht bei kleinen Feldern aus wie ein
 * Zeichenfehler.
 *
 * **Keine Schrift.** Ein Zeichensatz kann fehlen, eine Glyphe anders aussehen oder anders
 * zentriert sein; die Figur ist Spielinformation und darf davon nicht abhängen.
 */

import type { ReactElement } from 'react';

import type { PieceType } from '@flaghex/engine';

export interface SymbolOptions {
  /** Der Radius, in dem das Symbol Platz hat — üblicherweise etwas kleiner als die Figur. */
  readonly r: number;
  readonly color: string;
}

/** Strichstärke im Verhältnis zum Symbolradius — bei jeder Größe gleich kräftig. */
const STROKE_RATIO = 0.22;

export function pieceSymbol(type: PieceType, { r, color }: SymbolOptions): ReactElement {
  const k = r / 10;
  const width = r * STROKE_RATIO;
  const line = {
    fill: 'none',
    stroke: color,
    strokeWidth: width,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (type) {
    case 'flagBearer':
      return (
        <g data-symbol="flagBearer">
          <path d={`M ${-3.6 * k} ${-8 * k} V ${8 * k}`} {...line} />
          <path
            d={`M ${-3.6 * k} ${-7.4 * k} L ${6.8 * k} ${-4.1 * k} L ${-3.6 * k} ${-0.8 * k} Z`}
            fill={color}
          />
        </g>
      );
    case 'guard':
      return (
        <g data-symbol="guard">
          <path
            d={
              `M 0 ${-8.2 * k} L ${6.6 * k} ${-5.2 * k} V ${0.8 * k} ` +
              `C ${6.6 * k} ${5 * k} ${3.4 * k} ${7.2 * k} 0 ${8.3 * k} ` +
              `C ${-3.4 * k} ${7.2 * k} ${-6.6 * k} ${5 * k} ${-6.6 * k} ${0.8 * k} ` +
              `V ${-5.2 * k} Z`
            }
            fill={color}
          />
        </g>
      );
    case 'runner':
      return (
        <g data-symbol="runner">
          <path d={`M ${-7 * k} ${-5.6 * k} L ${-1.2 * k} 0 L ${-7 * k} ${5.6 * k}`} {...line} />
          <path d={`M ${1.2 * k} ${-5.6 * k} L ${7 * k} 0 L ${1.2 * k} ${5.6 * k}`} {...line} />
        </g>
      );
    case 'jumper':
      return (
        <g data-symbol="jumper">
          <path d={`M ${-6.8 * k} ${5.4 * k} Q 0 ${-9.6 * k} ${6.8 * k} ${5.4 * k}`} {...line} />
          <circle cx={0} cy={6.2 * k} r={1.8 * k} fill={color} />
        </g>
      );
    case 'pusher':
      return (
        <g data-symbol="pusher">
          <path d={`M ${-7.2 * k} ${-6.2 * k} V ${6.2 * k}`} {...line} />
          <path d={`M ${-3.2 * k} 0 H ${5.4 * k}`} {...line} />
          <path d={`M ${2 * k} ${-3.6 * k} L ${6 * k} 0 L ${2 * k} ${3.6 * k}`} {...line} />
        </g>
      );
  }
}
